// src/pages/billing-overview/components/EditInvoiceModal.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const INVOICE_STATUSES = [
  
  { value: 'pending', label: 'Envoyée' },

  { value: 'paid', label: 'Payée' },
  { value: 'overdue', label: 'En retard' },
  { value: 'cancelled', label: 'Annulée' },
];

const PAYMENT_METHODS = [
  'Virement bancaire',
  'Chèque',
  'Espèces',
  'Carte bancaire',
  'Prélèvement automatique',
  'PayPal',
  'Autre',
];

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
      {label}{required && <span className="text-destructive ml-1">*</span>}
    </label>
    {children}
  </div>
);

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-colors';

const EditInvoiceModal = ({ invoice, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    invoice_number: '',
    client_name: '',
    client_email: '',
    status: 'draft',
    issue_date: '',
    due_date: '',
    payment_date: '',
    subtotal: '',
    tax_percentage: 0,
    tax_amount: '',
    discount_amount: '',
    total_amount: '',
    amount_paid: '',
    balance_due: '',
    payment_method: '',
    notes: '',
    invoice_type: '',
  });

  // Seed form from invoice prop
  useEffect(() => {
    if (!invoice) return;
    setForm({
      invoice_number: invoice.invoice_number || invoice.invoiceNumber || '',
      client_name: invoice.client_name || invoice.clientName || '',
      client_email: invoice.client_email || invoice.clientEmail || '',
      status: invoice.status || 'draft',
      issue_date: invoice.issue_date || invoice.issueDate || '',
      due_date: invoice.due_date || invoice.dueDate || '',
      payment_date: invoice.payment_date || invoice.paymentDate || '',
      subtotal: invoice.subtotal ?? '',
      tax_percentage: invoice.tax_percentage ?? 0,
      tax_amount: invoice.tax_amount ?? '',
      discount_amount: invoice.discount_amount ?? '',
      total_amount: invoice.total_amount || invoice.totalAmount || '',
      amount_paid: invoice.amount_paid || invoice.amountPaid || '',
      balance_due: invoice.balance_due || invoice.balanceDue || '',
      payment_method: invoice.payment_method || invoice.paymentMethod || '',
      notes: invoice.notes || '',
      invoice_type: invoice.invoice_type || '',
    });
  }, [invoice]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Auto-recalculate derived fields when financials change
  const recalculate = (updated) => {
    const subtotal = parseFloat(updated.subtotal) || 0;
    const taxPct = parseFloat(updated.tax_percentage) || 0;
    const discount = parseFloat(updated.discount_amount) || 0;
    const taxAmount = parseFloat(((subtotal * taxPct) / 100).toFixed(2));
    const total = parseFloat((subtotal + taxAmount - discount).toFixed(2));
    const amountPaid = parseFloat(updated.amount_paid) || 0;
    const balanceDue = parseFloat((total - amountPaid).toFixed(2));

    return {
      ...updated,
      tax_amount: taxAmount,
      total_amount: total < 0 ? 0 : total,
      balance_due: balanceDue < 0 ? 0 : balanceDue,
    };
  };

  const handleFinancialChange = (key, value) => {
    const updated = recalculate({ ...form, [key]: value });
    setForm(updated);
  };

  const handleSubmit = async () => {
    if (!form.invoice_number.trim()) return toast.error('Le numéro de facture est requis');
    if (!form.client_name.trim()) return toast.error('Le nom du client est requis');
    if (!form.issue_date) return toast.error("La date d'émission est requise");
    if (!form.due_date) return toast.error("La date d'échéance est requise");

    try {
      setSaving(true);

      const payload = {
        invoice_number: form.invoice_number.trim(),
        client_name: form.client_name.trim(),
        client_email: form.client_email.trim() || null,
        status: form.status,
        issue_date: form.issue_date,
        due_date: form.due_date,
        payment_date: form.payment_date || null,
        subtotal: parseFloat(form.subtotal) || 0,
        tax_percentage: parseInt(form.tax_percentage) || 0,
        tax_amount: parseFloat(form.tax_amount) || 0,
        discount_amount: parseFloat(form.discount_amount) || 0,
        total_amount: parseFloat(form.total_amount) || 0,
        amount_paid: parseFloat(form.amount_paid) || 0,
        balance_due: parseFloat(form.balance_due) || 0,
        payment_method: form.payment_method || null,
        notes: form.notes.trim() || null,
        invoice_type: form.invoice_type.trim() || null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('invoices')
        .update(payload)
        .eq('id', invoice.id);

      if (error) throw error;

      toast.success('Facture mise à jour avec succès !');
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error updating invoice:', err);
      toast.error('Erreur lors de la mise à jour : ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="FileEdit" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Modifier la facture</h2>
              <p className="text-xs text-muted-foreground">{form.invoice_number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

          {/* ── Section: Identification ── */}
          <section>
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-2">
              <Icon name="FileText" size={13} className="text-primary" />
              Identification
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="N° de facture" required>
                <input
                  className={inputClass}
                  value={form.invoice_number}
                  onChange={(e) => set('invoice_number', e.target.value)}
                  placeholder="INV-2024-001"
                />
              </Field>
              <Field label="Type de facture">
                <input
                  className={inputClass}
                  value={form.invoice_type}
                  onChange={(e) => set('invoice_type', e.target.value)}
                  placeholder="ex: phase, avoir..."
                />
              </Field>
              <Field label="Statut" required>
                <select
                  className={inputClass}
                  value={form.status}
                  onChange={(e) => set('status', e.target.value)}
                >
                  {INVOICE_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          {/* ── Section: Client ── */}
          <section>
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-2">
              <Icon name="User" size={13} className="text-primary" />
              Client
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nom du client" required>
                <input
                  className={inputClass}
                  value={form.client_name}
                  onChange={(e) => set('client_name', e.target.value)}
                  placeholder="Dr. Dupont"
                />
              </Field>
              <Field label="Email du client">
                <input
                  type="email"
                  className={inputClass}
                  value={form.client_email}
                  onChange={(e) => set('client_email', e.target.value)}
                  placeholder="client@exemple.fr"
                />
              </Field>
            </div>
          </section>

          {/* ── Section: Dates ── */}
          <section>
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-2">
              <Icon name="Calendar" size={13} className="text-primary" />
              Dates
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Date d'émission" required>
                <input
                  type="date"
                  className={inputClass}
                  value={form.issue_date}
                  onChange={(e) => set('issue_date', e.target.value)}
                />
              </Field>
              <Field label="Date d'échéance" required>
                <input
                  type="date"
                  className={inputClass}
                  value={form.due_date}
                  onChange={(e) => set('due_date', e.target.value)}
                />
              </Field>
              <Field label="Date de paiement">
                <input
                  type="date"
                  className={inputClass}
                  value={form.payment_date}
                  onChange={(e) => set('payment_date', e.target.value)}
                />
              </Field>
            </div>
          </section>

          {/* ── Section: Montants ── */}
          <section>
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-2">
              <Icon name="DollarSign" size={13} className="text-primary" />
              Montants
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Sous-total HT" required>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={inputClass}
                  value={form.subtotal}
                  onChange={(e) => handleFinancialChange('subtotal', e.target.value)}
                />
              </Field>
              <Field label="TVA (%)">
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  className={inputClass}
                  value={form.tax_percentage}
                  onChange={(e) => handleFinancialChange('tax_percentage', e.target.value)}
                />
              </Field>
              <Field label="Montant TVA">
                <input
                  type="number"
                  step="0.01"
                  className={inputClass + ' bg-muted/40 cursor-not-allowed'}
                  value={form.tax_amount}
                  readOnly
                />
              </Field>
              <Field label="Remise">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={inputClass}
                  value={form.discount_amount}
                  onChange={(e) => handleFinancialChange('discount_amount', e.target.value)}
                />
              </Field>
              <Field label="Total TTC">
                <input
                  type="number"
                  step="0.01"
                  className={inputClass + ' bg-muted/40 cursor-not-allowed font-bold text-primary'}
                  value={form.total_amount}
                  readOnly
                />
              </Field>
              <Field label="Montant payé">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={inputClass}
                  value={form.amount_paid}
                  onChange={(e) => handleFinancialChange('amount_paid', e.target.value)}
                />
              </Field>
              <Field label="Solde dû">
                <input
                  type="number"
                  step="0.01"
                  className={inputClass + ' bg-muted/40 cursor-not-allowed'}
                  value={form.balance_due}
                  readOnly
                />
              </Field>
            </div>

            {/* Live summary strip */}
            <div className="mt-4 grid grid-cols-3 gap-3">
              {[
                { label: 'Total TTC', value: form.total_amount, color: 'text-primary' },
                { label: 'Payé', value: form.amount_paid, color: 'text-success' },
                { label: 'Solde dû', value: form.balance_due, color: parseFloat(form.balance_due) > 0 ? 'text-destructive' : 'text-success' },
              ].map((item) => (
                <div key={item.label} className="bg-muted/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                  <p className={`text-base font-bold ${item.color}`}>
                    {parseFloat(item.value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Section: Paiement & Notes ── */}
          <section>
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 flex items-center gap-2">
              <Icon name="CreditCard" size={13} className="text-primary" />
              Paiement & Notes
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Méthode de paiement">
                <select
                  className={inputClass}
                  value={form.payment_method}
                  onChange={(e) => set('payment_method', e.target.value)}
                >
                  <option value="">— Sélectionner —</option>
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Notes internes">
                <textarea
                  rows={3}
                  className={inputClass + ' resize-none'}
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="Notes visibles sur la facture..."
                />
              </Field>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0 bg-muted/20">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={saving}
            loading={saving}
            iconName="Save"
            iconPosition="left"
          >
            Enregistrer les modifications
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditInvoiceModal;